<template>
  <div v-if="villa">
    <b-modal
      id="solmar-villas-modal"
      ref="solmar-villas-modal-ref"
      hide-footer
      title="Villa Information"
    >
      <div class="modal-banner-container" v-if="villa.images.length > 0">
        <template>
          <div>
            <h4 class="mt-3 orange">Gallery</h4>
            <b-carousel
              id="carousel-1"
              v-model="slide"
              :interval="0"
              controls
              indicators
              background="#ababab"
              img-width="1024"
              img-height="480"
              @sliding-start="onSlideStart"
              @sliding-end="onSlideEnd"
            >
              <div v-for="(image, index) in villa.images" :key="index">
                <b-carousel-slide :img-src="image"></b-carousel-slide>
              </div>
            </b-carousel>
          </div>
        </template>
        <div class="modal-banner-header-text">
          {{ villa.name }}
        </div>
        <div class="modal-banner-subheader-text">
          {{ villa.locationName }}
        </div>
      </div>
      <hr />
      <div>
        <h4 class="mt-3 orange text-center">Trailfinders View</h4>
      </div>

      <div>
        <h4 class="mt-2 orange">About the Villa</h4>
        <p class="html-dark-text" v-html="villa.aboutHtml"></p>
      </div>

      <h4 class="orange text-center mt-4">Villa Details</h4>

      <div class="mt-2">
        <h4 class="orange">Accommodation</h4>
        <div
          class="mt-3"
          v-for="area in Object.keys(villa.groupedSpaces)"
          :key="area"
        >
          <p class="my-0 font-weight-bold">{{ area }}</p>

          <div class="d-flex flex-wrap justify-content-between">
            <div
              class="col-6 d-flex flex-column mb-3 px-0"
              v-for="subarea in villa.groupedSpaces[area]"
              :key="subarea.name"
            >
              <div>
                <div>
                  <p class="underlined pl-1 mb-0">{{ subarea.name }}</p>
                </div>
                <div v-for="feature in subarea.features" :key="feature">
                  <li v-if="customFeatureFilter(feature, subarea.features)">
                    {{ formatFeatures(feature) }}
                  </li>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-2" v-if="villa.checkIn && villa.checkOut">
        <h4 class="orange">Important Information</h4>
        <p class="mb-0">Check In : {{ villa.checkIn }}</p>
        <p>Check Out : {{ villa.checkOut }}</p>
        <span v-if="villa.additionalSafetyInfo">
          <p v-for="(addInfo, i) in villa.additionalSafetyInfo" :key="i">
            {{ addInfo.description }}
          </p>
        </span>
        <span v-if="villa.mobilityHtml">
          <p class="underlined mb-1">Mobility Information</p>
          <p v-html="villa.mobilityHtml"></p>
        </span>
        <p></p>
      </div>

      <div
        class="mt-2"
        v-if="villa.mapData && villa.mapData.lng && villa.mapData.lat"
      >
        <Map
          :mapData="villa.mapData"
          :initOnMount="true"
          :hideFoodMarkers="true"
        />
      </div>

      <div class="mt-2" v-if="villa.nearby.length">
        <h4 class="orange">Location</h4>
        Below you will find some distances to the nearest amenities and
        attractions.
        <b-row v-for="item in villa.nearby" :key="item.where">
          <b-col class="col-3 font-weight-bold"
            ><li>{{ item.where }}</li></b-col
          >
          <b-col class="col-9">{{ item.formatted }}</b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>
<script>
import Map from "@/partials/map/map";

export default {
  components: {
    Map,
  },
  props: {
    villaData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      villa: null,
      slide: 0,
      sliding: null,
    };
  },
  mounted() {
    setTimeout(() => {
      this.formatVillaData();
    }, 1000);
  },
  methods: {
    show() {
      this.$refs["solmar-villas-modal-ref"].show();
    },
    formatVillaData() {
      const tmp = {};
      tmp.name = this.villaData.villaName;
      tmp.locationName = this.villaData.region;
      const {
        images,
        description,
        spaces,
        checkIn,
        checkOut,
        googleLocation,
        distances,
        mobilityStatement,
        errataList,
      } = this.villaData.accommodation;
      tmp.images = images.map((img) => img.url);
      tmp.aboutHtml = description;
      tmp.groupedSpaces = this.formatAccommodationSpaces(spaces);
      if (checkIn && checkIn.length === 2) {
        tmp.checkIn = `${checkIn[0]}:${checkIn[1] === 0 ? "00" : checkIn[1]}`;
      }
      if (checkOut && checkOut.length === 2) {
        tmp.checkOut = `${checkOut[0]}:${
          checkOut[1] === 0 ? "00" : checkOut[1]
        }`;
      }
      tmp.mobilityHtml =
        mobilityStatement && !mobilityStatement.includes("<p>null") // some mobility data has html of <p>null</p>
          ? mobilityStatement
          : "";
      tmp.additionalSafetyInfo =
        errataList && errataList.length ? errataList : null;
      tmp.mapData = {
        lat: parseFloat(googleLocation.latitude),
        lng: parseFloat(googleLocation.longitude),
        zoom: 14,
      };
      tmp.nearby = distances.map((dist) => {
        dist.formatted = `${String(dist.value)} ${this.capitalise(
          dist.unit
        )} by ${dist.travelBy.toLowerCase()}`;
        let where = dist.where.split("_");
        dist.where = where.map((word) => this.capitalise(word)).join(" ");
        if (dist.where === "Golf") {
          dist.where = "Golf Course";
        }
        return dist;
      });
      this.villa = tmp;
    },
    formatAccommodationSpaces(spaces) {
      const grouped = Object.groupBy(spaces, ({ floor }) =>
        this.capitalise(floor)
      );
      return grouped;
    },
    formatFeatures(feature) {
      let splitFeature = feature.split("_");
      const capitalised = splitFeature
        .filter((word) => word)
        .map((word) => this.capitalise(word));
      return capitalised.join(" ");
    },
    customFeatureFilter(name, features) {
      // some features are not useful, e.g. DOUBLE, when DOUBLE_BED is provided as a feature as well.
      // Manually removes these features from the list to avoid duplication
      if (
        name &&
        ((name.toUpperCase() === "DOUBLE" && features.includes("DOUBLE_BED")) ||
          (name.toUpperCase() === "COMFORTABLE_SEATING" &&
            features.includes("COMFORTABLE_SEATING_AREA")) ||
          (name.toUpperCase() === "SHOWER" &&
            features.includes("WALK_IN_SHOWER")))
      ) {
        return false;
      }
      return true;
    },
    formatArrDepDate(_date) {},
    capitalise(str) {
      if (!str) {
        return str;
      }
      const splitStr = str.split("_");
      return splitStr
        .map(
          (_str) =>
            `${_str.slice(0, 1).toUpperCase()}${_str.slice(1).toLowerCase()}`
        )
        .join(" ");
    },
    onSlideStart() {
      this.sliding = true;
    },
    onSlideEnd() {
      this.sliding = false;
    },
  },
};
</script>

<style scoped>
.modal-banner-container {
  position: relative;
  text-align: center;
  color: white;
  font-weight: 500;
}

.modal-banner-header-text,
.modal-banner-subheader-text {
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  background-color: rgba(175, 171, 171, 0.5);
}
.modal-banner-header-text {
  font-size: 3vw;
  top: 30%;
}
.modal-banner-subheader-text {
  font-size: 1.5vw;
  top: 75%;
}
.vtd-center-image {
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 95% !important;
}
.orange {
  color: orange;
  font-weight: bolder;
}
.important-info-container {
  background-color: #dbdbdb;
  padding: 10px;
}
.html-dark-text :deep(p) {
  color: black !important;
  font-size: 1rem !important;
  font-weight: 300 !important;
}
.underlined {
  text-decoration: underline;
}
</style>
